import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  Output,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy, inject,
} from '@angular/core';
import { SpinnerService } from '../../../../core/service/spinner.service';
import { ButtonShowIcon, ButtonSize, ButtonTooltip, ButtonType } from "./button.util";
import { ICONS_FOLDER_PATH } from "../all-icons/all-icons.util";
import { NgClass } from "@angular/common";

@Component({
  selector: 'app-button',
  imports: [
    NgClass,
  ],
  templateUrl: './button.component.html',
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
/**
 * Standard button component, use for any type of button
 *
 * @var type to set background color
 * @var showIcon to define if and where to show the icon
 * @var bordersSquaredShape defines if the corners are more squared or not;<br>not needed for 'input-only' icon button!
 * @var iconName to set the icon name, e.g. iconName = 'copy.svg' will use copy.svg icon
 * @var size to set size of the button; for 'input-only' icon sets 'xsmall' by default!
 *
 * IMPORTANT: The @Input disabled will prevent click only when using @Output buttonClick
 */
export class ButtonComponent implements OnChanges {
  private spinnerService: SpinnerService = inject(SpinnerService);

  @Input() type: ButtonType = 'default';
  @Input() size: ButtonSize;
  @Input() showIcon: ButtonShowIcon = 'left';
  @Input() additionalClasses: string;
  @Input() bordersSquaredShape: 'right' | 'left' | 'full' | 'input-only-squared' | 'full-small-button' | '' = '';
  @Input() iconName: string;
  @Input() disabled = false;
  @Input() buttonTooltip: ButtonTooltip;
  @Input() showSpinner = false;

  @ViewChild('spinnerDiv') spinnerDiv: ElementRef;

  @Output() buttonClick = new EventEmitter<void>();

  iconSize: string;
  iconPath: string;
  showTooltip = false;
  tooltipCoordinates: DOMRect;
  /**
   * This boolean flag is used to adjust the button's width and row-gap when the spinner is active.
   * It can also be utilized to change the button's text dynamically in the parent component using `ViewChild`.
   * For example, you could change the text from "Update" (when `spinnerButtonClicked` is false)
   * to "Updating" (when `spinnerButtonClicked` is true) inside your parent component.
   */
  spinnerButtonClicked = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.iconName || changes.type) {
      if (changes.iconName) {
        this.iconPath = ICONS_FOLDER_PATH + this.iconName;
      }
      this.setIconSize();
    }
  }

  buttonClickEmitter(): void {
    if (this.disabled) {
      return;
    }

    this.buttonClick.emit();
    if (this.showSpinner) {
      this.startSpinner();
    }
  }

  /**
   * Automatically starts the spinner animation inside the button, if `showSpinner` is true and button was clicked.
   * Sets `spinnerButtonClicked` to true, indicating the spinner is active.
   */
  startSpinner(): void {
    this.spinnerButtonClicked = true;
    this.spinnerService.startLoadingForComponent(this.spinnerDiv);
  }

  /**
   * Stops the spinner animation on the button.
   * **Note**:
   * Although the spinner starts automatically, stopping the spinner requires calling this method explicitly.
   * Utilize `ViewChild` in the parent component to call `stopSpinner` on the button component.
   */
  stopSpinner(): void {
    this.spinnerButtonClicked = false;
    this.spinnerService.finishLoadingForComponent(this.spinnerDiv);
  }

  private setIconSize(): void {
    if (this.showIcon === 'input-only') {
      this.iconSize = 'input-only';
      this.bordersSquaredShape = 'input-only-squared';
      this.size = 'xsmall';
      return;
    }
    this.iconSize = 'n-' + this.showIcon + '-icon';
    if (this.size === 'xsmall') {
      this.iconSize = 'xs-' + this.showIcon + '-icon';
    } else if (this.size?.includes('xxsmall')) {
      this.iconSize = 'xxs-' + this.showIcon + '-icon';
    } else if (this.size) {
      this.iconSize = this.size.charAt(0) + '-' + this.showIcon + '-icon';
    }
    if (this.size === 'modal-container-button') {
      this.iconSize = 'xs-left-icon';
    }
  }
}
